import { stageName } from '../../../vocabulary';
import { featureName } from '../features';
import type { T, UiLocale } from '../../../messages';

/**
 * **What happened, said as a sentence** — the one place an audit row becomes
 * words (console redesign, phase B6; plan §1, principle 2).
 *
 * The trail records `tenant.modules` with `{ enable: ["certificates",
 * "reporting"] }`; an operator reads "Jose turned on gift cards and reports".
 * Every screen that shows the trail — the Activity tab, the Log, the
 * Overview's "Lately" — goes through `sentenceOf`, so a code is worded once and
 * the three can never disagree about what a row meant.
 *
 * **Every action anything writes is here** (`KNOWN_ACTIONS`, and a
 * table-driven test that walks it): `admin-api`'s routes, `core-api`'s staff
 * writes, the CLI and the importer. One this list has never heard of — a newer
 * server mid-rollout — is still a sentence, made from its own code ("did “set
 * grid” (scheduling)"), never the bare code standing where a sentence should.
 *
 * **Facts may be missing, and the sentence must stay true without them.** The
 * Activity tab reads the server's *summary* of a row, which drops every key
 * that could name a person (`name`, `email`, …) and every list of records; the
 * Log reads the full detail. So each branch reads what it can, and the
 * catalog's entries take `''` or `-1` for "not known" and say the shorter
 * sentence: "created the business" rather than "created undefined".
 *
 * The subject is separate (`who`), because the screens set it in bold and
 * because it is decided by a different rule: the actor string's kind.
 */

/** The four fields of an audit row a sentence is made from — both wires have them. */
export interface AuditRow {
  action: string;
  actor: string;
  target: string;
  /** The full detail (the Log) or the server's summary of it (Activity). */
  detail: Record<string, unknown>;
}

export interface Sentence {
  /** Who did it, as a person reads it: "Jose", "Their staff". */
  who: string;
  /** What they did, lower-case, no subject and no full stop. */
  what: string;
  /** True when the row is only somebody looking — the screens set these quieter. */
  look: boolean;
}

/**
 * The reads the console's own server records (`registry.ts`, `write: false`).
 * Listed here rather than asked for, because the sentence has to know on a
 * screen that has no registry; the table test pins the list against the codes.
 */
export const LOOK_ACTIONS: readonly string[] = [
  'fleet.list',
  'fleet.health',
  'audit.view',
  'tenant.view',
  'tenant.health',
  'tenant.activity',
  'tenant.readiness',
  'catalog.view',
  'intake.view',
  'intake.list',
  'intake.uploads',
  'intake.draft',
  'intake.catalog_draft',
  'key.list',
  'webhook.list',
  'delivery.list',
  'domain.list',
  'branding.view',
  'concierge.view',
  'staff.list',
  'job.executions',
  'job.execution',
  // The page builder's check before a publish — a POST that stores nothing.
  'branding.check',
];

/**
 * Every action code written anywhere in the repo, as of the redesign: found by
 * grepping `action: '…'` and `recordAudit(` across `apps/` and `packages/`
 * (plus the two built at runtime, `staff.disable` / `staff.enable`, and the
 * CLI's fleet-wide `operator.grant` / `operator.revoke`). A new code should be
 * added here and given a sentence; the test fails for one that is here without.
 */
export const KNOWN_ACTIONS: readonly string[] = [
  ...LOOK_ACTIONS,
  'tenant.create',
  'tenant.set_status',
  'tenant.modules',
  'tenant.protect',
  'tenant.delete',
  'tenant.settings',
  'tenant.scheduling_policy',
  'tenant.locales',
  'tenant.support_session',
  'tenant.exported',
  'tenant.export_landed',
  'catalog.create',
  'catalog.commerce',
  'catalog.presentation',
  'catalog.media',
  'catalog.category',
  'catalog.option',
  'catalog.add_on',
  'catalog.merge_options',
  'catalog.roles',
  'catalog.seed',
  'intake.mint',
  'intake.extend',
  'intake.reopen',
  'intake.close',
  'intake.apply_hours',
  'intake.apply_closures',
  'intake.remove_closure',
  'intake.promote',
  'intake.reject',
  'key.issue',
  'key.revoke',
  'webhook.set',
  'delivery.requeue',
  'domain.add',
  'domain.status',
  'domain.remove',
  'branding.publish',
  'branding.rollback',
  'concierge.enable',
  'concierge.disable',
  'concierge.settings',
  'concierge.status',
  'concierge.sync',
  'job.run',
  'staff.create',
  'staff.invite',
  'staff.resend',
  'staff.revoke',
  'staff.remove',
  'staff.set_role',
  'staff.disable',
  'staff.enable',
  'staff.record',
  'operator.grant',
  'operator.revoke',
  'album.create',
  'album.delete',
  'album.notify',
  'album.regenerate',
  'album.revoke',
  'client.anonymize',
  'data_request.file',
  'data_request.run',
  'data_request.cancel',
  'departures.waiver_published',
  'departures.waiver_withdrawn',
  'lodging.ical_feed_mint',
  'lodging.ical_feed_revoke',
  'messaging.newsletter',
  'messaging.owner',
  'messaging.owner_alerts',
  'messaging.reminders',
  'ordering.policy',
  'ordering.timing',
  'payment.config',
  'fulfillment.credentials',
  'shop.fulfilment',
  'shop.payment',
  'shop.pickup_expiry',
  'till.tax',
  'till.tenders',
  'till.tips',
  'booking.mode',
  'scheduling.grid',
  'import.run',
  'import.appointments',
  'import.contacts',
  'import.clover_customers',
  'roster.seed',
  'roster.reset',
  // Added after the redesign's first pass: their app's look (B8), direct
  // photo upload (B7), and who may change what they sell (B10).
  'branding.app_look',
  'tenant.asset_upload',
  'catalog.media_upload',
  'tenant.catalog_editors',
  'item.photo.add',
  'item.photo.arrange',
];

/** True for a row that is only somebody looking. */
export function isLook(action: string): boolean {
  return LOOK_ACTIONS.includes(action);
}

/* ------------------------------------------------------------------ */
/* Who                                                                 */
/* ------------------------------------------------------------------ */

/**
 * The actor, as a person reads it.
 *
 * `operator:jose@jswaves.com` is "Jose" — the address's local part, split on
 * dots and dashes and capitalised, because that is how a colleague is named in
 * a sentence (the full address is in the raw record). `staff:<uid>` is "Their
 * staff": the uid names a login, not a person, and the console holds no grant
 * that would turn it into one. The CLI's and the importer's own names are
 * words; anything else is the part after the colon, or the string itself.
 */
export function actorName(t: T, actor: string): string {
  const colon = actor.indexOf(':');
  const kind = colon < 0 ? actor : actor.slice(0, colon);
  const rest = colon < 0 ? '' : actor.slice(colon + 1).trim();
  switch (kind) {
    case 'operator':
      return rest === '' || rest === 'unknown'
        ? t('activity.actor.operatorUnknown')
        : personOf(rest);
    case 'staff':
      return t('activity.actor.staff');
    case 'importer':
      return t('activity.actor.importer');
    case 'system':
      return t('activity.actor.system');
    case 'ci':
      return t('activity.actor.ci');
    default:
      return rest === '' ? actor : rest;
  }
}

function personOf(handle: string): string {
  const local = handle.includes('@') ? handle.slice(0, handle.indexOf('@')) : handle;
  const words = local
    .split(/[._-]+/)
    .filter((w) => w !== '')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.length === 0 ? handle : words.join(' ');
}

/** The kind of actor a row's `who` filter sorts it under. */
export function actorKind(actor: string): 'business' | 'operator' | 'other' {
  if (actor.startsWith('staff:')) return 'business';
  if (actor.startsWith('operator:')) return 'operator';
  return 'other';
}

/* ------------------------------------------------------------------ */
/* What                                                                */
/* ------------------------------------------------------------------ */

export function sentenceOf(t: T, locale: UiLocale, row: AuditRow): Sentence {
  return {
    who: actorName(t, row.actor),
    what: describeAction(t, locale, row),
    look: isLook(row.action),
  };
}

/** The sentence's predicate — what the actor did — for one row. */
export function describeAction(t: T, locale: UiLocale, row: AuditRow): string {
  const d = row.detail;
  const w = words(t, locale);

  switch (row.action) {
    /* ---- the business itself ---------------------------------------- */
    case 'tenant.create':
      return t('activity.s.tenant.create', { name: str(d, 'name') });
    case 'tenant.set_status': {
      const from = str(d, 'previous_status');
      const to = str(d, 'status');
      if (to === '') return t('activity.s.tenant.set_status.some');
      return t('activity.s.tenant.set_status', {
        from: from === '' ? '' : stageName(t, from),
        to: stageName(t, to),
      });
    }
    case 'tenant.modules': {
      const parts: string[] = [];
      const on = strings(d, 'enable');
      const trial = strings(d, 'trial');
      const off = strings(d, 'disable');
      if (on.length > 0) parts.push(t('activity.s.tenant.modules.on', { list: w.features(on) }));
      if (trial.length > 0) {
        parts.push(t('activity.s.tenant.modules.trial', { list: w.features(trial) }));
      }
      if (off.length > 0) parts.push(t('activity.s.tenant.modules.off', { list: w.features(off) }));
      return parts.length === 0 ? t('activity.s.tenant.modules.none') : parts.join('; ');
    }
    case 'tenant.protect':
      return d['protected'] === false
        ? t('activity.s.tenant.protect.off')
        : t('activity.s.tenant.protect.on');
    case 'tenant.delete':
      return t('activity.s.tenant.delete', { name: str(d, 'name') });
    case 'tenant.settings':
      return t('activity.s.tenant.settings', { count: changedKeys(d) });
    case 'tenant.scheduling_policy':
      return t('activity.s.tenant.scheduling_policy');
    case 'tenant.locales': {
      const after = strings(d, 'after');
      return t('activity.s.tenant.locales', { list: after.length === 0 ? '' : w.languages(after) });
    }
    case 'tenant.support_session': {
      const seconds = num(d, 'ttl_seconds');
      return t('activity.s.tenant.support_session', {
        minutes: seconds <= 0 ? 15 : Math.round(seconds / 60),
      });
    }
    case 'tenant.exported':
      return t('activity.s.tenant.exported', { rows: num(d, 'total_rows') });
    case 'tenant.export_landed':
      return t('activity.s.tenant.export_landed', { source: str(d, 'source_tenant_id') });

    /* ---- what they sell ---------------------------------------------- */
    case 'catalog.create': {
      const details = [
        num(d, 'duration_min') > 0 ? t('activity.minutes', { n: num(d, 'duration_min') }) : '',
        has(d, 'price_cents') ? w.money(num(d, 'price_cents')) : '',
      ].filter((s) => s !== '');
      return t('activity.s.catalog.create', {
        what: w.kind(str(d, 'kind')),
        details: details.join(', '),
      });
    }
    case 'catalog.commerce': {
      const before = obj(d, 'before');
      const after = obj(d, 'after');
      const name = str(before, 'name');
      return t('activity.s.catalog.commerce', {
        what: name === '' ? t('activity.anItem') : t('activity.named', { name }),
        changes: w.changes(before, after),
      });
    }
    case 'catalog.presentation':
      return t('activity.s.catalog.presentation', { what: t('activity.anItem') });
    case 'catalog.media':
      return t('activity.s.catalog.media', {
        what: t('activity.anItem'),
        count: has(d, 'images') && d['images'] !== null ? num(d, 'images') : -1,
      });
    case 'catalog.category':
      return t('activity.s.catalog.category', {
        from: str(d, 'from'),
        to: str(d, 'to'),
        count: num(d, 'items'),
      });
    case 'catalog.option': {
      const created = obj(d, 'created');
      if (Object.keys(created).length > 0 || has(d, 'created')) {
        const details = [
          num(created, 'duration_min') > 0
            ? t('activity.minutes', { n: num(created, 'duration_min') })
            : '',
          has(created, 'price_cents') ? w.money(num(created, 'price_cents')) : '',
        ].filter((s) => s !== '');
        return t('activity.s.catalog.option.add', { details: details.join(', ') });
      }
      return t('activity.s.catalog.option.change', {
        changes: w.changes(obj(d, 'before'), obj(d, 'after')),
      });
    }
    case 'catalog.add_on':
      if (d['unlinked'] === true) return t('activity.s.catalog.add_on.unlink');
      return has(d, 'before') && d['before'] !== null
        ? t('activity.s.catalog.add_on.change')
        : t('activity.s.catalog.add_on.link');
    case 'catalog.merge_options': {
      // The full row carries the lists; the summary keeps only their lengths.
      const absorbed = listLength(d, 'absorbed');
      const lengths = listLength(d, 'options');
      return t('activity.s.catalog.merge_options', {
        rows: absorbed < 0 ? -1 : absorbed + 1,
        lengths,
      });
    }
    case 'catalog.roles':
      return t('activity.s.catalog.roles');
    case 'catalog.seed': {
      if (d['dry_run'] === true) return t('activity.s.catalog.seed.dry');
      const counted = ['items', 'menu_items', 'retail_items', 'stays', 'departures'].filter((k) =>
        has(d, k),
      );
      return t('activity.s.catalog.seed', {
        count: counted.length === 0 ? -1 : counted.reduce((sum, k) => sum + num(d, k), 0),
      });
    }

    /* ---- the questionnaire ------------------------------------------- */
    case 'intake.mint': {
      const details = [
        str(d, 'locale') === '' ? '' : w.language(str(d, 'locale')),
        str(d, 'template') === ''
          ? ''
          : t('activity.template', { name: str(d, 'template').replace(/[_-]+/g, ' ') }),
      ].filter((s) => s !== '');
      return d['rotated'] === true
        ? t('activity.s.intake.mint.rotated', { details: details.join(', ') })
        : t('activity.s.intake.mint', { details: details.join(', ') });
    }
    case 'intake.extend':
      return t('activity.s.intake.extend', { days: num(d, 'days') });
    case 'intake.reopen':
      return t('activity.s.intake.reopen');
    case 'intake.close':
      return t('activity.s.intake.close');
    case 'intake.apply_hours':
      return t('activity.s.intake.apply_hours');
    case 'intake.apply_closures':
      return t('activity.s.intake.apply_closures', {
        count: listLength(d, 'days') >= 0 ? listLength(d, 'days') : listLength(d, 'blackout_ids'),
      });
    case 'intake.remove_closure':
      return t('activity.s.intake.remove_closure');
    case 'intake.promote':
      return t('activity.s.intake.promote');
    case 'intake.reject':
      return t('activity.s.intake.reject');

    /* ---- partners ---------------------------------------------------- */
    case 'key.issue': {
      const env = str(d, 'env');
      return t('activity.s.key.issue', {
        env: env === 'live' ? t('activity.env.live') : env === 'test' ? t('activity.env.test') : '',
        partner: w.partner(str(d, 'integration')),
      });
    }
    case 'key.revoke':
      return t('activity.s.key.revoke', { partner: w.partner(str(d, 'integration')) });
    case 'webhook.set': {
      const partner = w.partner(str(d, 'integration'));
      if (d['active'] === false) return t('activity.s.webhook.set.off', { partner });
      return d['created'] === true
        ? t('activity.s.webhook.set.new', { partner })
        : t('activity.s.webhook.set.change', { partner });
    }
    case 'delivery.requeue': {
      const counted = has(d, 'requeued') || has(d, 'created');
      return t('activity.s.delivery.requeue', {
        since: str(d, 'since') === '' ? '' : w.date(str(d, 'since')),
        count: counted ? num(d, 'requeued') + num(d, 'created') : -1,
      });
    }
    case 'concierge.enable':
      return t('activity.s.concierge.enable');
    case 'concierge.disable':
      return t('activity.s.concierge.disable');
    case 'concierge.settings':
      return t('activity.s.concierge.settings');
    case 'concierge.status':
      return str(d, 'status') === 'paused'
        ? t('activity.s.concierge.status.paused')
        : t('activity.s.concierge.status.enabled');
    case 'concierge.sync':
      return t('activity.s.concierge.sync');

    /* ---- the website ------------------------------------------------- */
    case 'domain.add':
      return has(d, 'moved_from')
        ? t('activity.s.domain.add.moved', { host: row.target })
        : t('activity.s.domain.add', { host: row.target });
    case 'domain.status':
      return t('activity.s.domain.status', {
        host: row.target,
        status: w.domainStatus(str(d, 'status')),
      });
    case 'domain.remove':
      return t('activity.s.domain.remove', { host: row.target });
    case 'branding.publish':
      return t('activity.s.branding.publish', {
        version: has(d, 'branding_version') ? num(d, 'branding_version') : -1,
      });
    case 'branding.rollback':
      return t('activity.s.branding.rollback', {
        version: has(d, 'restored_from') ? num(d, 'restored_from') : -1,
      });

    case 'branding.app_look':
      return d['cleared'] === true
        ? t('activity.s.branding.app_look.cleared')
        : t('activity.s.branding.app_look');
    case 'tenant.asset_upload': {
      const what =
        str(d, 'kind') === 'staff-background'
          ? t('activity.asset.staffBackground')
          : t('activity.asset.other');
      return str(d, 'step') === 'mint'
        ? t('activity.s.tenant.asset_upload.start', { what })
        : t('activity.s.tenant.asset_upload', { what });
    }

    /* ---- photos of what they sell ------------------------------------ */
    case 'catalog.media_upload':
      // Two rows per photo: the upload asked for (`mint`), then kept (`publish`).
      return str(d, 'step') === 'mint'
        ? t('activity.s.catalog.media_upload.start', { what: t('activity.anItem') })
        : t('activity.s.catalog.media_upload', { what: t('activity.anItem') });
    case 'item.photo.add':
      return t('activity.s.item.photo.add', { what: photoSubject(t, str(d, 'kind')) });
    case 'item.photo.arrange': {
      const what = photoSubject(t, str(d, 'kind'));
      const removed = listLength(d, 'removed');
      if (removed > 0) return t('activity.s.item.photo.arrange.removed', { what, count: removed });
      if (listLength(d, 'website') > 0) return t('activity.s.item.photo.arrange.website', { what });
      return t('activity.s.item.photo.arrange', { what });
    }
    case 'tenant.catalog_editors':
      return str(d, 'after') === 'owner_and_staff'
        ? t('activity.s.tenant.catalog_editors.staff')
        : str(d, 'after') === 'owner'
          ? t('activity.s.tenant.catalog_editors.owner')
          : t('activity.s.tenant.catalog_editors');

    /* ---- jobs -------------------------------------------------------- */
    case 'job.run':
    case 'job.executions':
    case 'job.execution': {
      const job = str(d, 'job') === '' ? (row.target.split(':')[0] ?? '') : str(d, 'job');
      const named =
        job === 'conformance'
          ? t('activity.job.conformance')
          : job === 'import'
            ? d['dry_run'] === true
              ? t('activity.job.importTrial')
              : t('activity.job.import')
            : t('activity.job.named', { name: job });
      if (row.action === 'job.run') return t('activity.s.job.run', { job: named });
      return row.action === 'job.executions'
        ? t('activity.s.job.executions', { job: named })
        : t('activity.s.job.execution', { job: named });
    }

    /* ---- people ------------------------------------------------------ */
    case 'staff.record': {
      const who = str(d, 'email') === '' ? t('activity.someone') : str(d, 'email');
      return t('activity.s.staff.record', { who, role: w.role(str(d, 'role')) });
    }
    case 'staff.create':
    case 'staff.invite':
    case 'staff.set_role': {
      const who = str(d, 'email') === '' ? t('activity.someone') : str(d, 'email');
      const role = w.role(str(d, 'role'));
      if (row.action === 'staff.create') return t('activity.s.staff.create', { who, role });
      return row.action === 'staff.invite'
        ? t('activity.s.staff.invite', { who, role })
        : t('activity.s.staff.set_role', { who, role });
    }
    case 'staff.resend':
    case 'staff.revoke':
    case 'staff.remove':
    case 'staff.disable':
    case 'staff.enable': {
      const who = str(d, 'email') === '' ? t('activity.someone') : str(d, 'email');
      switch (row.action) {
        case 'staff.resend':
          return t('activity.s.staff.resend', { who });
        case 'staff.revoke':
          return t('activity.s.staff.revoke', { who });
        case 'staff.remove':
          return t('activity.s.staff.remove', { who });
        case 'staff.disable':
          return t('activity.s.staff.disable', { who });
        default:
          return t('activity.s.staff.enable', { who });
      }
    }
    case 'operator.grant':
      return t('activity.s.operator.grant', { who: row.target || t('activity.someone') });
    case 'operator.revoke':
      return t('activity.s.operator.revoke', { who: row.target || t('activity.someone') });

    /* ---- the rest of the business's settings, set from the CLI -------- */
    case 'album.create':
      return t('activity.s.album.create');
    case 'album.delete':
      return t('activity.s.album.delete', { count: has(d, 'photos') ? num(d, 'photos') : -1 });
    case 'album.notify':
      return t('activity.s.album.notify');
    case 'album.regenerate':
      return t('activity.s.album.regenerate');
    case 'album.revoke':
      return t('activity.s.album.revoke');
    case 'client.anonymize':
      return t('activity.s.client.anonymize');
    case 'data_request.file':
      return str(d, 'kind') === 'export'
        ? t('activity.s.data_request.file.export')
        : str(d, 'kind') === 'delete'
          ? t('activity.s.data_request.file.delete')
          : t('activity.s.data_request.file');
    case 'data_request.run':
      return t('activity.s.data_request.run');
    case 'data_request.cancel':
      return t('activity.s.data_request.cancel');
    case 'departures.waiver_published': {
      const to = obj(d, 'to');
      return t('activity.s.departures.waiver_published', {
        version: has(to, 'version') ? num(to, 'version') : -1,
      });
    }
    case 'departures.waiver_withdrawn':
      return t('activity.s.departures.waiver_withdrawn');
    case 'lodging.ical_feed_mint':
      return t('activity.s.lodging.ical_feed_mint');
    case 'lodging.ical_feed_revoke':
      return t('activity.s.lodging.ical_feed_revoke');
    case 'messaging.newsletter':
      return has(d, 'after') && d['after'] === null
        ? t('activity.s.messaging.newsletter.off')
        : t('activity.s.messaging.newsletter');
    case 'messaging.owner':
      return t('activity.s.messaging.owner');
    case 'messaging.owner_alerts':
      return t('activity.s.messaging.owner_alerts');
    case 'messaging.reminders':
      return t('activity.s.messaging.reminders');
    case 'ordering.policy':
      return t('activity.s.ordering.policy');
    case 'ordering.timing':
      return t('activity.s.ordering.timing');
    case 'payment.config':
      return str(d, 'environment') === 'sandbox'
        ? t('activity.s.payment.config.test')
        : t('activity.s.payment.config');
    case 'fulfillment.credentials':
      return t('activity.s.fulfillment.credentials');
    case 'shop.fulfilment':
      return t('activity.s.shop.fulfilment');
    case 'shop.payment':
      return t('activity.s.shop.payment');
    case 'shop.pickup_expiry':
      return t('activity.s.shop.pickup_expiry');
    case 'till.tax':
      return has(d, 'after') && d['after'] === null
        ? t('activity.s.till.tax.off')
        : t('activity.s.till.tax');
    case 'till.tenders':
      return t('activity.s.till.tenders');
    case 'till.tips':
      return t('activity.s.till.tips');
    case 'booking.mode':
      return t('activity.s.booking.mode');
    case 'scheduling.grid':
      return t('activity.s.scheduling.grid');

    /* ---- imports and the test roster ---------------------------------- */
    case 'import.run':
      return d['reconciliation_passed'] === true
        ? t('activity.s.import.run.ok')
        : d['reconciliation_passed'] === false
          ? t('activity.s.import.run.off')
          : t('activity.s.import.run');
    case 'import.appointments':
      return t('activity.s.import.appointments', {
        booked: has(d, 'booked') ? num(d, 'booked') : -1,
        refused: num(d, 'refused'),
      });
    case 'import.contacts':
      return t('activity.s.import.contacts', {
        created: has(d, 'created') ? num(d, 'created') : -1,
        matched: num(d, 'matched'),
      });
    case 'import.clover_customers':
      return t('activity.s.import.clover_customers', {
        created: has(d, 'created') ? num(d, 'created') : -1,
        matched: num(d, 'matched'),
      });
    case 'roster.seed':
      return t('activity.s.roster.seed');
    case 'roster.reset':
      return t('activity.s.roster.reset');

    /* ---- looks ------------------------------------------------------- */
    case 'fleet.list':
      return t('activity.s.fleet.list');
    case 'fleet.health':
      return t('activity.s.fleet.health');
    case 'audit.view':
      return t('activity.s.audit.view');
    case 'tenant.view':
      return t('activity.s.tenant.view');
    case 'tenant.health':
      return t('activity.s.tenant.health');
    case 'tenant.activity':
      return t('activity.s.tenant.activity');
    case 'tenant.readiness':
      return t('activity.s.tenant.readiness');
    case 'catalog.view':
      return t('activity.s.catalog.view');
    case 'intake.view':
      return t('activity.s.intake.view');
    case 'intake.list':
      return t('activity.s.intake.list');
    case 'intake.uploads':
      return t('activity.s.intake.uploads');
    case 'intake.draft':
      return t('activity.s.intake.draft');
    case 'intake.catalog_draft':
      return t('activity.s.intake.catalog_draft');
    case 'key.list':
      return t('activity.s.key.list');
    case 'webhook.list':
      return t('activity.s.webhook.list');
    case 'delivery.list':
      return t('activity.s.delivery.list');
    case 'domain.list':
      return t('activity.s.domain.list');
    case 'branding.view':
      return t('activity.s.branding.view');
    case 'branding.check':
      return t('builder.activity.check');
    case 'concierge.view':
      return t('activity.s.concierge.view');
    case 'staff.list':
      return t('activity.s.staff.list');

    default:
      return humanised(t, row.action);
  }
}

/**
 * A code nobody has worded yet, as words: `scheduling.set_grid` is "did “set
 * grid” (scheduling)". Readable, obviously mechanical, and never the code on
 * its own — the raw record beside it still has the exact string.
 */
export function humanised(t: T, action: string): string {
  const [noun = '', ...rest] = action.split('.');
  const spaced = (s: string): string => s.replace(/[_-]+/g, ' ').trim();
  const verb = spaced(rest.join(' '));
  if (verb === '') return t('activity.s.unknown', { verb: spaced(noun), noun: spaced(noun) });
  return t('activity.s.unknown', { verb, noun: spaced(noun) });
}

/* ------------------------------------------------------------------ */
/* Words a sentence is made of                                          */
/* ------------------------------------------------------------------ */

function words(t: T, locale: UiLocale) {
  const list = (items: string[]): string => {
    try {
      return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
    } catch {
      return items.join(', ');
    }
  };
  const language = (code: string): string => {
    try {
      return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
    } catch {
      return code;
    }
  };
  const money = (cents: number): string =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      cents / 100,
    );
  return {
    list,
    language,
    languages: (codes: string[]) => list(codes.map(language)),
    money,
    date: (iso: string): string => {
      const parsed = new Date(iso);
      if (Number.isNaN(parsed.getTime())) return iso;
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(parsed);
    },
    features: (modules: string[]) => list(modules.map((m) => featureName(t, m))),
    kind: (kind: string): string => {
      switch (kind) {
        case 'scheduled':
          return t('activity.kind.scheduled');
        case 'stocked':
          return t('activity.kind.stocked');
        case 'prepared':
          return t('activity.kind.prepared');
        case 'stay':
          return t('activity.kind.stay');
        case 'departure':
          return t('activity.kind.departure');
        default:
          return t('activity.anItem');
      }
    },
    partner: (integration: string): string =>
      integration === 'aiconcierge' || integration === ''
        ? t('activity.partner.aiconcierge')
        : t('activity.partner.named', { name: integration }),
    role: (role: string): string => {
      switch (role) {
        case 'owner':
          return t('activity.role.owner');
        case 'readonly':
          return t('activity.role.readonly');
        default:
          return t('activity.role.staff');
      }
    },
    domainStatus: (status: string): string => {
      switch (status) {
        case 'pending_dns':
          return t('activity.domainStatus.pending_dns');
        case 'issuing':
          return t('activity.domainStatus.issuing');
        case 'live':
          return t('activity.domainStatus.live');
        default:
          return status.replace(/_/g, ' ');
      }
    },
    /** A before/after pair of an item or an option, as "price from 120.00 to 125.00, …". */
    changes: (before: Record<string, unknown>, after: Record<string, unknown>): string => {
      const out: string[] = [];
      if (has(after, 'name') && str(after, 'name') !== str(before, 'name')) {
        out.push(t('activity.change.renamed', { to: str(after, 'name') }));
      }
      if (has(after, 'price_cents') && num(after, 'price_cents') !== num(before, 'price_cents')) {
        out.push(
          t('activity.change.price', {
            from: has(before, 'price_cents') ? money(num(before, 'price_cents')) : '',
            to: money(num(after, 'price_cents')),
          }),
        );
      }
      if (
        has(after, 'duration_min') &&
        num(after, 'duration_min') !== num(before, 'duration_min')
      ) {
        out.push(
          t('activity.change.length', {
            from: has(before, 'duration_min') ? String(num(before, 'duration_min')) : '',
            to: t('activity.minutes', { n: num(after, 'duration_min') }),
          }),
        );
      }
      if (after['active'] === false && before['active'] !== false) {
        out.push(t('activity.change.offSale'));
      }
      if (after['active'] === true && before['active'] === false) {
        out.push(t('activity.change.onSale'));
      }
      if (after['is_default'] === true && before['is_default'] !== true) {
        out.push(t('activity.change.headline'));
      }
      return out.join(', ');
    },
  };
}

/** The staff app's photo routes name the item by its mount: a product or a service. */
function photoSubject(t: T, noun: string): string {
  switch (noun) {
    case 'product':
      return t('activity.kind.stocked');
    case 'service':
      return t('activity.kind.scheduled');
    default:
      return t('activity.anItem');
  }
}

/* ------------------------------------------------------------------ */
/* Reading a detail that may be partial                                */
/* ------------------------------------------------------------------ */

function has(d: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(d, key);
}

function str(d: Record<string, unknown>, key: string): string {
  const v = d[key];
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}

function num(d: Record<string, unknown>, key: string): number {
  const v = d[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return 0;
}

function obj(d: Record<string, unknown>, key: string): Record<string, unknown> {
  const v = d[key];
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function strings(d: Record<string, unknown>, key: string): string[] {
  const v = d[key];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/** A list's length, from the list or from the summary's `<key>_count`; -1 when neither. */
function listLength(d: Record<string, unknown>, key: string): number {
  const v = d[key];
  if (Array.isArray(v)) return v.length;
  if (has(d, `${key}_count`)) return num(d, `${key}_count`);
  return -1;
}

/**
 * How many settings a `before` / `after` write changed — top-level keys of
 * `after` whose value differs from `before`'s. Zero when the pair is not there
 * (a summary that dropped it), and the sentence then says "saved".
 */
function changedKeys(d: Record<string, unknown>): number {
  const before = obj(d, 'before');
  const after = obj(d, 'after');
  return Object.keys(after).filter((k) => JSON.stringify(after[k]) !== JSON.stringify(before[k]))
    .length;
}
